import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/authService.js'
import { userPlaylistService } from '@/services/userPlaylistService.js'

export const useAuthStore = defineStore('auth', () => {
  // 상태
  const user = ref(null)
  const token = ref(null)
  const isLoading = ref(false)
  const lastPlaylist = ref([])
  
  // 계산된 값
  const isLoggedIn = computed(() => !!user.value)
  const userName = computed(() => user.value?.name || '')
  const userEmail = computed(() => user.value?.email || '')
  
  // 액션
  const login = async (userData, authToken = null) => {
    try {
      isLoading.value = true
      
      // 사용자 정보 저장
      user.value = {
        id: userData.id,
        name: userData.name || userData.nickname,
        email: userData.email,
        role: userData.role || 'user',  // Role 추가
        provider: userData.provider,
        profileImage: userData.profileImage || userData.profileImageUrl || '',
        loginAt: new Date().toISOString()
      }
      
      // 토큰 저장
      if (authToken) {
        token.value = authToken
        authService.setToken(authToken)
      }
      
      // 로컬스토리지에 저장 (새로고침 시 유지)
      const authData = {
        user: user.value,
        token: token.value
      }
      localStorage.setItem('ttaechang_auth', JSON.stringify(authData))
      
      // 로그인 후 재생목록 복원
      console.log('🔑 로그인 완료 - 재생목록 초기화 시작')
      await initializePlaylist()

      // Player에게 재생목록 로드 요청
      window.dispatchEvent(new CustomEvent('auth-playlist-loaded'))

      console.log('로그인 성공:', user.value)
      
    } catch (error) {
      console.error('로그인 실패:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }
  
  const logout = async () => {
    try {
      // Player에게 로그아웃 전 저장 요청
      console.log('🔄 로그아웃 전 Player 재생목록 저장 요청')
      window.dispatchEvent(new CustomEvent('auth-logout-before-save'))

      // 저장 시간을 위해 잠시 대기
      await new Promise(resolve => setTimeout(resolve, 200))

      // 서버에 로그아웃 요청 (세션 삭제)
      if (isLoggedIn.value && token.value) {
        try {
          console.log('🚪 서버에 로그아웃 요청 중...')
          await authService.logout()
          console.log('✅ 서버 로그아웃 완료 - 세션 삭제됨')
        } catch (error) {
          console.warn('⚠️ 서버 로그아웃 API 실패 (클라이언트 로그아웃은 계속 진행):', error)
          // 서버 로그아웃 실패해도 클라이언트 로그아웃은 진행
        }
      }

      // Player 재생목록 초기화 이벤트 발생
      window.dispatchEvent(new CustomEvent('auth-logout'))

      // 플레이어 상태 초기화 (재생 상태 포함)
      console.log('🎵 플레이어 상태 초기화...')
      localStorage.removeItem('player_playback_state')
      sessionStorage.removeItem('player_playback_state')
      localStorage.removeItem('oauth_result')
      sessionStorage.removeItem('oauth_return_url')
      sessionStorage.removeItem('oauth_provider')

      // 상태 초기화
      user.value = null
      token.value = null
      lastPlaylist.value = []

      // localStorage 전체 초기화
      console.log('🧹 localStorage 전체 초기화...')
      localStorage.clear()

      // sessionStorage 전체 초기화
      console.log('🧹 sessionStorage 전체 초기화...')
      sessionStorage.clear()

      // JWT 토큰 제거
      authService.removeToken()

      console.log('✅ 로그아웃 완료 - 모든 데이터 초기화됨')

      // 라우터로 홈으로 이동 (새로고침 방지)
      // window.location.reload() 대신 사용

    } catch (error) {
      console.error('로그아웃 오류:', error)
      // 오류가 있어도 로그아웃은 진행
      user.value = null
      token.value = null
      lastPlaylist.value = []

      // 오류 시 localStorage 완전 초기화
      localStorage.clear()
      sessionStorage.clear()
      authService.removeToken()

      // 라우터로 홈으로 이동 (새로고침 방지)
      // window.location.reload() 대신 사용
    }
  }
  
  const initializeAuth = () => {
    try {
      // AuthService 초기화 (JWT 토큰 설정)
      authService.initializeAuth()

      // 강제 로그아웃 이벤트 리스너 등록
      window.addEventListener('force-logout', async (event) => {
        const { reason, message } = event.detail || {}
        console.log('🚨 강제 로그아웃 이벤트 감지:', { reason, message })

        // 이미 로그아웃된 상태라면 무시
        if (!isLoggedIn.value) {
          console.log('이미 로그아웃된 상태 - 강제 로그아웃 무시')
          return
        }

        // 완전한 로그아웃 처리 (알림 없이)
        await logout()

        console.log('강제 로그아웃 완료 - 홈으로 이동')
      })

      // 페이지 로드 시 저장된 인증 정보 복원
      const savedAuth = localStorage.getItem('ttaechang_auth')
      if (savedAuth) {
        const authData = JSON.parse(savedAuth)
        user.value = authData.user
        token.value = authData.token

        // 토큰이 있으면 authService에 설정 (게스트 모드는 토큰이 null일 수 있음)
        if (token.value) {
          authService.setToken(token.value)
        }

        console.log('저장된 인증 정보 복원:', user.value)
        console.log('게스트 모드:', user.value?.provider === 'guest' ? '예' : '아니오')
      } else {
        // 기존 호환성을 위해 구버전 데이터 확인
        const savedUser = localStorage.getItem('ttaechang_user')
        if (savedUser) {
          user.value = JSON.parse(savedUser)
          console.log('기존 사용자 정보 복원:', user.value)
        }
      }

      // 페이지 로드 시 재생목록 복원은 별도 함수에서 처리

    } catch (error) {
      console.error('인증 초기화 오류:', error)
      // 오류 발생 시 저장된 데이터 정리
      localStorage.removeItem('ttaechang_auth')
      localStorage.removeItem('ttaechang_user')
      localStorage.removeItem('ttaechang_last_playlist')
      authService.removeToken()
    }
  }
  
  // 중복 저장 방지를 위한 변수들
  let lastSavedPlaylistHash = null
  let savePlaylistTimeout = null

  const saveCurrentPlaylist = async (playlist = []) => {
    try {
      if (!isLoggedIn.value) {
        console.log('비로그인 사용자는 재생목록을 저장할 수 없습니다')
        return
      }

      // 로컬스토리지에만 저장 (실시간 DB 저장 제거)
      const playlistData = {
        userId: user.value.id,
        playlist: playlist,
        savedAt: new Date().toISOString()
      }

      localStorage.setItem('ttaechang_user_playlist', JSON.stringify(playlistData))
      lastPlaylist.value = [...playlist]
      console.log('📱 localStorage에 재생목록 저장됨:', lastPlaylist.value.length, '곡')

    } catch (error) {
      console.error('재생목록 저장 오류:', error)
    }
  }

  // 로그아웃/화면이동 시에만 DB 저장하는 별도 함수
  const savePlaylistToDatabase = async (playlist = []) => {
    try {
      if (!isLoggedIn.value || !user.value?.id) {
        console.log('로그인되지 않아 DB 저장을 건너뜁니다')
        return
      }

      // 게스트 모드인 경우 DB 저장 건너뜀
      if (user.value?.provider === 'guest') {
        console.log('게스트 모드 - 데이터베이스 저장 건너뜀 (로컬만 사용)')
        return
      }

      console.log('💾 데이터베이스에 재생목록 저장 시작:', playlist.length, '곡')
      await userPlaylistService.saveUserPlaylist(user.value.id, playlist)
      console.log('💾 데이터베이스 저장 완료:', playlist.length, '곡')
    } catch (error) {
      console.error('💾 데이터베이스 저장 실패:', error)
    }
  }
  
  const restoreLastPlaylist = async () => {
    try {
      if (!isLoggedIn.value) {
        console.log('비로그인 사용자는 재생목록을 사용할 수 없습니다')
        lastPlaylist.value = [] // 명시적으로 빈 배열
        return []
      }

      // 게스트 모드인 경우에도 로컬 저장소는 사용
      if (user.value?.provider === 'guest') {
        console.log('게스트 모드 - 로컬 저장소만 사용, 서버 동기화는 건너뜀')
        // 로컬 저장소에서만 복원하고 서버 동기화는 건너뜀
        const savedData = localStorage.getItem('ttaechang_user_playlist')
        if (savedData) {
          try {
            const playlistData = JSON.parse(savedData)
            if (playlistData.userId === user.value.id && playlistData.playlist) {
              lastPlaylist.value = [...playlistData.playlist]
              console.log('게스트 모드 - 로컬 저장소에서 플레이리스트 복원:', lastPlaylist.value.length)
            }
          } catch (error) {
            console.error('게스트 모드 - 로컬 저장소 복원 실패:', error)
            lastPlaylist.value = []
          }
        } else {
          lastPlaylist.value = []
        }
        return lastPlaylist.value
      }

      console.log('🔄 재생목록 복원 시작 - 현재 길이:', lastPlaylist.value.length)

      // 먼저 기존 재생목록을 완전히 초기화
      lastPlaylist.value = []
      console.log('🧹 기존 재생목록 초기화 완료')

      // 손상된 localStorage 데이터 정리
      console.log('🧹 localStorage 데이터 정리 시작')
      const keysToCheck = ['ttaechang_user_playlist', 'ttaechang_last_playlist']
      keysToCheck.forEach(key => {
        const data = localStorage.getItem(key)
        if (data) {
          try {
            const parsed = JSON.parse(data)
            if (parsed.playlist && parsed.playlist.length > 100) {
              console.log(`⚠️ 손상된 데이터 발견 (${key}): ${parsed.playlist.length}곡 - 삭제합니다`)
              localStorage.removeItem(key)
            }
          } catch (e) {
            console.log(`⚠️ 손상된 JSON 데이터 삭제: ${key}`)
            localStorage.removeItem(key)
          }
        }
      })

      // localStorage에서 로드
      const savedData = localStorage.getItem('ttaechang_user_playlist')
      if (savedData) {
        try {
          const playlistData = JSON.parse(savedData)
          if (playlistData.userId === user.value.id && playlistData.playlist) {
            lastPlaylist.value = [...playlistData.playlist] // 새로운 배열로 복사
            console.log('📱 localStorage에서 재생목록 복원됨:', lastPlaylist.value.length, '곡')
            return lastPlaylist.value
          }
        } catch (e) {
          console.log('⚠️ localStorage 데이터 파싱 오류, 삭제합니다')
          localStorage.removeItem('ttaechang_user_playlist')
        }
      }

      // localStorage에 없으면 DB에서 로드
      console.log('💾 데이터베이스에서 재생목록 로드 중...')
      try {
        const playlist = await userPlaylistService.getUserPlaylist(user.value.id)

        if (playlist && Array.isArray(playlist) && playlist.length > 0) {
          const convertedPlaylist = playlist.map(song => ({
            id: song.id,
            youtubeId: song.youtubeId,
            videoId: song.youtubeId,
            title: song.title,
            artist: song.artist,
            thumbnailUrl: song.thumbnailUrl,
            thumbnail: song.thumbnailUrl,
            duration: song.duration,
            playOrder: song.playOrder
          }))

          lastPlaylist.value = [...convertedPlaylist] // 새로운 배열로 복사
          console.log('💾 데이터베이스에서 재생목록 복원됨:', lastPlaylist.value.length, '곡')

          // localStorage에 캐시
          const playlistData = {
            userId: user.value.id,
            playlist: convertedPlaylist,
            savedAt: new Date().toISOString()
          }
          localStorage.setItem('ttaechang_user_playlist', JSON.stringify(playlistData))

          return lastPlaylist.value
        } else {
          // 빈 재생목록인 경우 (새로운 사용자 또는 데이터베이스가 비어있는 경우)
          console.log('💾 데이터베이스에 저장된 재생목록이 없습니다 - 빈 재생목록으로 초기화')
          lastPlaylist.value = []

          // localStorage에도 빈 재생목록 저장하여 다음번 로딩 시간 절약
          const emptyPlaylistData = {
            userId: user.value.id,
            playlist: [],
            savedAt: new Date().toISOString()
          }
          localStorage.setItem('ttaechang_user_playlist', JSON.stringify(emptyPlaylistData))
          console.log('📱 빈 재생목록을 localStorage에 캐시함')

          return []
        }
      } catch (dbError) {
        console.error('💾 데이터베이스 재생목록 로드 실패:', dbError)

        // DB 에러 시에도 빈 재생목록으로 초기화
        lastPlaylist.value = []
        const emptyPlaylistData = {
          userId: user.value.id,
          playlist: [],
          savedAt: new Date().toISOString()
        }
        localStorage.setItem('ttaechang_user_playlist', JSON.stringify(emptyPlaylistData))
        console.log('📱 DB 오류로 인해 빈 재생목록으로 초기화')

        return []
      }

    } catch (error) {
      console.error('재생목록 복원 오류:', error)

      // 최종 예외 처리: 어떤 오류가 발생해도 빈 배열로 초기화
      lastPlaylist.value = []
      try {
        const emptyPlaylistData = {
          userId: user.value?.id || 0,
          playlist: [],
          savedAt: new Date().toISOString()
        }
        localStorage.setItem('ttaechang_user_playlist', JSON.stringify(emptyPlaylistData))
      } catch (storageError) {
        console.error('localStorage 저장 실패:', storageError)
      }

      return []
    }
  }
  
  const updateProfile = (profileData) => {
    try {
      if (!isLoggedIn.value) return

      // name이 업데이트되면 사용자 정보의 name 필드 업데이트
      if (profileData.nickname) {
        user.value = {
          ...user.value,
          name: profileData.nickname, // nickname을 name으로 매핑
          nickname: profileData.nickname,
          updatedAt: new Date().toISOString()
        }
      } else {
        user.value = {
          ...user.value,
          ...profileData,
          updatedAt: new Date().toISOString()
        }
      }

      // 로컬스토리지 업데이트 (ttaechang_auth와 ttaechang_user 모두)
      const authData = {
        user: user.value,
        token: token.value
      }
      localStorage.setItem('ttaechang_auth', JSON.stringify(authData))
      localStorage.setItem('ttaechang_user', JSON.stringify(user.value))

      console.log('프로필 업데이트됨:', user.value)

    } catch (error) {
      console.error('프로필 업데이트 오류:', error)
    }
  }

  const initializePlaylist = async () => {
    // 앱 초기화 시 재생목록 복원
    console.log('🎵 initializePlaylist 호출됨:', {
      isLoggedIn: isLoggedIn.value,
      playlistLength: lastPlaylist.value.length,
      user: user.value?.id
    })

    try {
      if (isLoggedIn.value) {
        console.log('🎵 로그인 상태 - 재생목록 복원 시작')
        await restoreLastPlaylist()
        console.log('🎵 재생목록 복원 완료:', lastPlaylist.value.length, '곡')

        // Player에게 재생목록 로드 완료 알림
        window.dispatchEvent(new CustomEvent('auth-playlist-loaded'))
      } else {
        console.log('🎵 비로그인 상태 - 재생목록 복원 건너뜀')
      }
    } catch (error) {
      console.error('🎵 initializePlaylist 오류:', error)
      lastPlaylist.value = []
    }
  }
  
  // 스토어 반환
  return {
    // 상태
    user,
    token,
    isLoading,
    lastPlaylist,
    
    // 계산된 값
    isLoggedIn,
    userName,
    userEmail,
    
    // 액션
    login,
    logout,
    initializeAuth,
    initializePlaylist,
    saveCurrentPlaylist,
    savePlaylistToDatabase,
    restoreLastPlaylist,
    updateProfile
  }
})